import axios, { AxiosInstance } from 'axios';

// In production: baked in as 'https://api.agenticqe.ai' at Docker build time (see Dockerfile.dashboard).
// For local dev, create admin-dashboard/.env.local with: REACT_APP_API_URL=http://localhost:8001
const API_BASE_URL = process.env.REACT_APP_API_URL ?? 'http://localhost:8001';

class ApiService {
  private client: AxiosInstance;

  constructor() {
    this.client = axios.create({
      baseURL: API_BASE_URL,
      headers: {
        'Content-Type': 'application/json',
      },
    });

    // Add token to requests
    this.client.interceptors.request.use(
      (config) => {
        const token = localStorage.getItem('admin_token');
        if (token) {
          config.headers.Authorization = `Bearer ${token}`;
        }
        return config;
      },
      (error) => Promise.reject(error)
    );

    // Handle token expiration
    this.client.interceptors.response.use(
      (response) => response,
      (error) => {
        if (error.response?.status === 401) {
          localStorage.removeItem('admin_token');
          localStorage.removeItem('admin_user');
          window.location.href = '/login';
        }
        return Promise.reject(error);
      }
    );
  }

  // Auth endpoints
  async checkSSOConfig() {
    const response = await this.client.get('/admin/auth/check');
    return response.data;
  }

  async initiateLogin() {
    window.location.href = `${API_BASE_URL}/admin/auth/login`;
  }

  async getCurrentUser() {
    const response = await this.client.get('/admin/auth/me');
    return response.data;
  }

  async logout() {
    await this.client.post('/admin/auth/logout');
    localStorage.removeItem('admin_token');
    localStorage.removeItem('admin_user');
  }

  async updateOwnPreferences(data: { timezone?: string }) {
    const response = await this.client.patch('/admin/auth/me/preferences', data);
    return response.data;
  }

  // User management (platform users: super_admin / admin / viewer)
  async listAdmins() {
    const response = await this.client.get('/admin/users/');
    return response.data;
  }

  async createAdmin(data: { email: string; name: string; role: string; ms_user_id?: string }) {
    const response = await this.client.post('/admin/users/', data);
    return response.data;
  }

  async getAdmin(email: string) {
    const response = await this.client.get(`/admin/users/${email}`);
    return response.data;
  }

  async updateAdmin(email: string, data: { name?: string; role?: string; is_active?: boolean; assistant_enabled?: boolean }) {
    const response = await this.client.patch(`/admin/users/${email}`, data);
    return response.data;
  }

  async retryEntraAssignment(email: string) {
    const response = await this.client.post(`/admin/users/${encodeURIComponent(email)}/retry-entra-assignment`);
    return response.data;
  }

  async setAssistantAccess(email: string, enabled: boolean) {
    const response = await this.client.post(`/admin/users/${encodeURIComponent(email)}/assistant-access`, { enabled });
    return response.data as { email: string; assistant_enabled: boolean };
  }

  async deleteAdmin(email: string) {
    const response = await this.client.delete(`/admin/users/${email}`);
    return response.data;
  }

  async getAdminStats() {
    const response = await this.client.get('/admin/users/stats/summary');
    return response.data;
  }

  // App viewer management
  async listAppViewers(appId: string): Promise<{ viewers: string[]; max_viewers: number }> {
    const response = await this.client.get(`/apps/${appId}/viewers`);
    return response.data;
  }

  async addAppViewer(appId: string, email: string) {
    const response = await this.client.post(`/apps/${appId}/viewers`, { email });
    return response.data;
  }

  async removeAppViewer(appId: string, viewerEmail: string) {
    const response = await this.client.delete(`/apps/${appId}/viewers/${encodeURIComponent(viewerEmail)}`);
    return response.data;
  }

  // App management
  async listApps() {
    const response = await this.client.get('/apps/list');
    return response.data;
  }

  async registerApp(data: any) {
    const response = await this.client.post('/apps/register', data);
    return response.data;
  }

  async fetchModels(openai_api_key?: string, anthropic_api_key?: string): Promise<{ models: string[]; endpoint_models: Record<string, string[]>; providers: string[] }> {
    const response = await this.client.post('/apps/fetch-models', { openai_api_key, anthropic_api_key });
    return { models: response.data.models, endpoint_models: response.data.endpoint_models ?? {}, providers: response.data.providers ?? [] };
  }

  async fetchModelsForApp(appId: string): Promise<{ models: string[]; endpoint_models: Record<string, string[]>; providers: string[] }> {
    const response = await this.client.post(`/apps/fetch-models-for-app/${appId}`);
    return { models: response.data.models, endpoint_models: response.data.endpoint_models ?? {}, providers: response.data.providers ?? [] };
  }

  async getEndpointCatalog(): Promise<{ key: string; description: string }[]> {
    const response = await this.client.get('/apps/catalog');
    return response.data.endpoints;
  }

  async updateApp(appId: string, data: any) {
    const response = await this.client.patch(`/apps/update/${appId}`, data);
    return response.data;
  }

  async updateAppKey(appId: string, openai_api_key: string) {
    const response = await this.client.patch(`/apps/update-key/${appId}`, { openai_api_key });
    return response.data;
  }

  async updateAppKeys(
    appId: string,
    keys: { openai_api_key?: string; anthropic_api_key?: string; remove_openai_key?: boolean; remove_anthropic_key?: boolean },
  ) {
    const response = await this.client.patch(`/apps/update-keys/${appId}`, keys);
    return response.data;
  }

  async searchDirectory(q: string): Promise<{ id: string; name: string; email: string; photo?: string | null }[]> {
    const response = await this.client.get(`/admin/users/search-directory?q=${encodeURIComponent(q)}`);
    return response.data.users;
  }

  async getAppInfo(appName: string) {
    const response = await this.client.get(`/apps/info?app_name=${appName}`);
    return response.data;
  }

  async deleteApp(appName: string) {
    const response = await this.client.delete(`/apps/delete/${appName}`);
    return response.data;
  }

  async getAppUsage() {
    const response = await this.client.get('/apps/usage');
    return response.data;
  }

  async getAppKey(appId: string): Promise<{ app_id: string; app_name: string; auth_key: string }> {
    const response = await this.client.get(`/apps/keys/${appId}`);
    return response.data;
  }

  async getAppLifetimeUsage(appId: string) {
    const response = await this.client.get(`/apps/lifetime-usage/${appId}`);
    return response.data;
  }

  async updateModelLimits(appId: string, modelLimits: Record<string, { input_tokens: number; output_tokens: number }>) {
    const response = await this.client.put(`/apps/update-model-limits/${appId}`, { model_limits: modelLimits });
    return response.data;
  }

  async retryModelCheck(appId: string) {
    const response = await this.client.post(`/apps/retry-model-check/${appId}`);
    return response.data;
  }

  // Requests (token limit / model access / app deletion)
  async listRequests(params?: { status?: string; type?: string; app_id?: string }) {
    const q = new URLSearchParams(params as any).toString();
    const response = await this.client.get(`/admin/requests${q ? '?' + q : ''}`);
    return response.data;
  }

  async getPendingRequestCount(): Promise<number> {
    const response = await this.client.get('/admin/requests/pending-count');
    return response.data.count;
  }

  async markRequestsViewed() {
    const response = await this.client.post('/admin/requests/mark-viewed');
    return response.data;
  }

  async submitTokenLimitRequest(app_id: string, requested_input_limit: number, requested_output_limit: number, reason?: string) {
    const response = await this.client.post('/admin/requests/token-limit', { app_id, requested_input_limit, requested_output_limit, reason });
    return response.data;
  }

  async submitModelAccessRequest(app_id: string, requested_models: string[], reason?: string) {
    const response = await this.client.post('/admin/requests/model-access', { app_id, requested_models, reason });
    return response.data;
  }

  async approveRequest(request_id: string, note?: string) {
    const response = await this.client.patch(`/admin/requests/${request_id}/approve`, { note });
    return response.data;
  }

  async rejectRequest(request_id: string, note?: string) {
    const response = await this.client.patch(`/admin/requests/${request_id}/reject`, { note });
    return response.data;
  }

  async initiateAppDeletion(app_id: string, note?: string) {
    const response = await this.client.post(`/admin/requests/delete-app/${app_id}`, { note });
    return response.data;
  }

  async cancelAppDeletion(app_id: string) {
    const response = await this.client.delete(`/admin/requests/delete-app/${app_id}/cancel`);
    return response.data;
  }

  async initiateAppRename(app_id: string, new_app_name: string, note?: string) {
    const response = await this.client.post(`/admin/requests/rename-app/${app_id}`, { new_app_name, note });
    return response.data;
  }

  async cancelAppRename(app_id: string) {
    const response = await this.client.delete(`/admin/requests/rename-app/${app_id}/cancel`);
    return response.data;
  }

  // ── Platform Assistant ───────────────────────────────────────────────────
  async getAssistantStatus(): Promise<{ enabled: boolean; model: string | null }> {
    const response = await this.client.get('/admin/assistant/status');
    return response.data;
  }

  /**
   * Stream a chat turn. Handlers fire for each SSE event.
   * Returns an AbortController the caller can use to cancel mid-stream.
   */
  streamAssistantChat(
    messages: Array<{ role: string; content: string }>,
    handlers: {
      onDelta?: (text: string) => void;
      onToolCall?: (payload: { name: string; id: string }) => void;
      onToolResult?: (payload: { name: string; id: string }) => void;
      onDone?: (payload: { finish_reason?: string }) => void;
      onError?: (message: string) => void;
    },
  ): AbortController {
    const controller = new AbortController();
    const token = localStorage.getItem('admin_token');

    (async () => {
      try {
        const resp = await fetch(`${API_BASE_URL}/admin/assistant/chat`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            ...(token ? { Authorization: `Bearer ${token}` } : {}),
          },
          body: JSON.stringify({ messages, stream: true }),
          signal: controller.signal,
        });

        if (!resp.ok) {
          const body = await resp.text().catch(() => '');
          handlers.onError?.(`Assistant request failed (${resp.status}): ${body || resp.statusText}`);
          return;
        }

        const reader = resp.body?.getReader();
        if (!reader) {
          handlers.onError?.('Assistant response has no body.');
          return;
        }
        const decoder = new TextDecoder();
        let buffer = '';
        while (true) {
          const { value, done } = await reader.read();
          if (done) break;
          buffer += decoder.decode(value, { stream: true });
          // Split on SSE frame delimiter
          let sep = buffer.indexOf('\n\n');
          while (sep !== -1) {
            const frame = buffer.slice(0, sep);
            buffer = buffer.slice(sep + 2);
            parseFrame(frame, handlers);
            sep = buffer.indexOf('\n\n');
          }
        }
        // Flush any trailing frame
        if (buffer.trim()) parseFrame(buffer, handlers);
      } catch (err: any) {
        if (err?.name === 'AbortError') return;
        handlers.onError?.(String(err?.message || err));
      }
    })();

    return controller;
  }

  // Admin operations
  async resetTokens() {
    const response = await this.client.post('/admin/reset-tokens');
    return response.data;
  }

  async retryCreditChecks(): Promise<{
    status: string; message?: string;
    probed?: number; still_exhausted_count?: number; still_exhausted_apps?: string[];
  }> {
    const response = await this.client.post('/admin/retry-credit-checks');
    return response.data;
  }

  async retryCreditCheckOneApp(appId: string): Promise<{
    status: string; found: boolean; app_id: string; app_name: string;
    openai_credits_exhausted: boolean; openai_credits_exhausted_message: string | null;
  }> {
    const response = await this.client.post(`/admin/retry-credit-check/${appId}`);
    return response.data;
  }

  async getLogs(params?: {
    page?: number;
    limit?: number;
    admin_email?: string;
    action?: string;
    resource_type?: string;
    start_date?: string;
    end_date?: string;
    search?: string;
  }) {
    const response = await this.client.get('/admin/logs', { params });
    return response.data;
  }

  async getAppActivity(appId: string, page = 1, limit = 50) {
    const response = await this.client.get(`/admin/logs/app/${appId}`, { params: { page, limit } });
    return response.data;
  }

  async runLoadTest() {
    const response = await this.client.post('/admin/run-load-test');
    return response.data;
  }

  async getLoadStats() {
    const response = await this.client.get('/admin/proxy-load-stats');
    return response.data;
  }

  // Model pricing
  async getPricing() {
    const response = await this.client.get('/admin/pricing');
    return response.data;
  }

  async syncPricing(force = false) {
    const response = await this.client.post(`/admin/pricing/sync?force=${force}`);
    return response.data;
  }

  async setPricing(data: { model: string; input_price: number; output_price: number; is_verified?: boolean }) {
    const response = await this.client.put('/admin/pricing', data);
    return response.data;
  }

  async deletePricing(model: string) {
    const response = await this.client.delete(`/admin/pricing/${encodeURIComponent(model)}`);
    return response.data;
  }

  // Analytics
  async getAnalyticsSummary(startDate?: string, endDate?: string) {
    const params = new URLSearchParams();
    if (startDate) params.append('start_date', startDate);
    if (endDate) params.append('end_date', endDate);
    const response = await this.client.get(`/admin/analytics/summary?${params}`);
    return response.data;
  }

  async getAnalyticsByPeriod(period: 'day' | 'week' | 'month', appId?: string) {
    const params = new URLSearchParams({ period });
    if (appId) params.append('app_id', appId);
    const response = await this.client.get(`/admin/analytics/by-period?${params}`);
    return response.data;
  }

  async getAnalyticsByModel(startDate?: string, endDate?: string, appId?: string) {
    const params = new URLSearchParams();
    if (startDate) params.append('start_date', startDate);
    if (endDate) params.append('end_date', endDate);
    if (appId) params.append('app_id', appId);
    const response = await this.client.get(`/admin/analytics/by-model?${params}`);
    return response.data;
  }

  async getAppAnalytics(appId: string, startDate?: string, endDate?: string) {
    const params = new URLSearchParams();
    if (startDate) params.append('start_date', startDate);
    if (endDate) params.append('end_date', endDate);
    const response = await this.client.get(`/admin/analytics/app/${appId}?${params}`);
    return response.data;
  }

  // Changelogs (super-admin only)
  async listPendingChangelogs(): Promise<{ auto_send_after_hours: number; items: ChangelogItem[] }> {
    const response = await this.client.get('/admin/changelogs/pending');
    return response.data;
  }

  async listChangelogHistory(): Promise<{ items: ChangelogItem[] }> {
    const response = await this.client.get('/admin/changelogs/history');
    return response.data;
  }

  async syncChangelogs() {
    const response = await this.client.post('/admin/changelogs/sync');
    return response.data;
  }

  async sendChangelogNow(changelogId: string) {
    const response = await this.client.post(`/admin/changelogs/${changelogId}/send`);
    return response.data;
  }

  async discardChangelog(changelogId: string) {
    const response = await this.client.post(`/admin/changelogs/${changelogId}/discard`);
    return response.data;
  }

  async getChangelogRecipients(): Promise<{ count: number; emails: string[] }> {
    const response = await this.client.get('/admin/changelogs/recipients');
    return response.data;
  }

  async listChangelogSubscribers(): Promise<{ items: ChangelogSubscriber[] }> {
    const response = await this.client.get('/admin/changelogs/subscribers');
    return response.data;
  }

  async addChangelogSubscriber(email: string) {
    const response = await this.client.post('/admin/changelogs/subscribers', { email });
    return response.data;
  }

  async removeChangelogSubscriber(email: string) {
    const response = await this.client.delete(`/admin/changelogs/subscribers/${encodeURIComponent(email)}`);
    return response.data;
  }

  // ── Endpoint status ─────────────────────────────────────────────────────
  async getEndpointStatus(): Promise<EndpointStatusSnapshot> {
    const response = await this.client.get('/admin/status/endpoints');
    return response.data;
  }

  async getEndpointStatusHistory(limit = 20): Promise<{ count: number; snapshots: EndpointStatusSnapshot[] }> {
    const response = await this.client.get(`/admin/status/endpoints/history?limit=${limit}`);
    return response.data;
  }

  async runEndpointStatusProbe(): Promise<EndpointStatusSnapshot> {
    const response = await this.client.post('/admin/status/endpoints/run');
    return response.data;
  }

  // ── Assistant configuration ────────────────────────────────────────────
  async getAssistantConfig(): Promise<AssistantConfig> {
    const response = await this.client.get('/admin/assistant/config');
    return response.data;
  }

  async updateAssistantConfig(patch: {
    openai_api_key?: string;
    token_limit_input?: number;
    token_limit_output?: number;
    weekly_token_limit_input?: number;
    weekly_token_limit_output?: number;
    monthly_token_limit_input?: number;
    monthly_token_limit_output?: number;
  }): Promise<AssistantConfig> {
    const response = await this.client.patch('/admin/assistant/config', patch);
    return response.data;
  }

  async initiateOrConfirmAssistantModelChange(new_model: string, note?: string) {
    const response = await this.client.post('/admin/requests/assistant-model-change', { new_model, note });
    return response.data;
  }

  async cancelAssistantModelChange() {
    const response = await this.client.delete('/admin/requests/assistant-model-change/cancel');
    return response.data;
  }
}

export interface AssistantUsageSummary {
  input_tokens: number;
  output_tokens: number;
  request_count: number;
}

export interface AssistantConfig {
  configured: boolean;
  enabled: boolean;
  model?: string | null;
  masked_openai_key?: string | null;
  openai_credits_exhausted: boolean;
  api_key_invalid: boolean;
  app_id?: string | null;
  token_limit_input?: number | null;
  token_limit_output?: number | null;
  weekly_token_limit_input?: number | null;
  weekly_token_limit_output?: number | null;
  monthly_token_limit_input?: number | null;
  monthly_token_limit_output?: number | null;
  usage_today: AssistantUsageSummary;
  usage_this_week: AssistantUsageSummary;
  usage_this_month: AssistantUsageSummary;
  daily_history: Array<{ date: string; input_tokens: number; output_tokens: number; request_count: number }>;
  model_usage: Record<string, { input_tokens: number; output_tokens: number; request_count: number }>;
  pending_model_change?: {
    request_id: string;
    current_model?: string;
    new_model: string;
    requested_by: string;
    approvals: string[];
    approvals_needed: number;
    created_at: string;
  } | null;
}

export interface EndpointProbeResult {
  app_id: string;
  app_name: string;
  endpoint: string;
  description?: string;
  model?: string | null;
  status_code: number;
  latency_ms: number;
  ok: boolean | null;
  error?: string | null;
  skipped?: boolean;
}

export interface EndpointStatusSnapshot {
  timestamp?: string;
  triggered_by?: string;
  results: EndpointProbeResult[];
  note?: string;
  ok?: boolean;
}

export type ChangelogStatus = 'pending_approval' | 'sent' | 'discarded' | 'failed';

export interface ChangelogItem {
  id: string;
  version: string;
  release_date: string;
  changelog_html: string;
  changelog_markdown: string;
  status: ChangelogStatus;
  auto_send_after: string | null;
  created_at: string | null;
  sent_at: string | null;
  sent_by: string | null;
  recipient_count: number | null;
  failure_reason: string | null;
  source_file: string | null;
}

export interface ChangelogSubscriber {
  email: string;
  added_by: string;
  added_at: string | null;
  enabled: boolean;
}

function parseFrame(
  raw: string,
  handlers: {
    onDelta?: (text: string) => void;
    onToolCall?: (payload: { name: string; id: string }) => void;
    onToolResult?: (payload: { name: string; id: string }) => void;
    onDone?: (payload: { finish_reason?: string }) => void;
    onError?: (message: string) => void;
  },
): void {
  let event = 'message';
  const dataLines: string[] = [];
  for (const line of raw.split('\n')) {
    if (line.startsWith('event:')) event = line.slice(6).trim();
    else if (line.startsWith('data:')) dataLines.push(line.slice(5).trim());
  }
  const dataStr = dataLines.join('\n');
  if (!dataStr) return;
  let payload: any;
  try { payload = JSON.parse(dataStr); } catch { payload = dataStr; }
  switch (event) {
    case 'delta':      handlers.onDelta?.(payload?.content ?? ''); break;
    case 'tool_call':  handlers.onToolCall?.(payload); break;
    case 'tool_result':handlers.onToolResult?.(payload); break;
    case 'done':       handlers.onDone?.(payload || {}); break;
    case 'error':      handlers.onError?.(payload?.message ?? String(payload)); break;
    default: break;
  }
}

const apiService = new ApiService();
export default apiService;
